import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { Editor } from '@tinymce/tinymce-react';
import { useSnackbar } from 'notistack';
import { KeenIcon } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';
import { I18N_LANGUAGES } from '@/i18n';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import { formatDisplayDateTime } from '@/utils/Date';
import {
  useGenerateCMSFAQDraftsMutation,
  usePublishCMSFAQTranslationMutation,
  useRollbackCMSFAQTranslationMutation,
  useUpdateCMSFAQTranslationDraftMutation,
} from '@/services/CmsFAQService';
import { CMSFAQItem, CMSFAQTranslationVariant } from '@/types/cms.types';

interface CMSFAQTranslationWorkflowSectionProps {
  item: CMSFAQItem;
  onRefresh: () => Promise<unknown>;
}

type DraftEditorState = {
  title: string;
  content: string;
  isExpanded: boolean;
  isEditingTitle: boolean;
  isEditingContent: boolean;
};

const statusClasses: Record<CMSFAQTranslationVariant['status'], string> = {
  missing: 'badge badge-outline',
  draft: 'badge badge-warning badge-outline',
  published: 'badge badge-success badge-outline',
};

const getLanguageLabel = (languageCode: string) =>
  I18N_LANGUAGES.find((language) => language.code === languageCode)?.label ||
  languageCode.toUpperCase();

const getLanguageFlag = (languageCode: string) =>
  I18N_LANGUAGES.find((language) => language.code === languageCode)?.flag ||
  '/media/flags/globe.svg';

const getEditorSeed = (variant?: CMSFAQTranslationVariant | null) => ({
  title:
    (variant?.status === 'published'
      ? variant?.published?.title || variant?.draft?.title
      : variant?.draft?.title || variant?.published?.title) || '',
  content:
    (variant?.status === 'published'
      ? variant?.published?.content || variant?.draft?.content
      : variant?.draft?.content || variant?.published?.content) || '',
});

const formatWorkflowTimestamp = (value?: string | null) =>
  value ? formatDisplayDateTime(value) : 'No timestamp';

const CMSFAQTranslationWorkflowSection = ({
  item,
  onRefresh,
}: CMSFAQTranslationWorkflowSectionProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const hasDraftPermission = hasPermission('cms_management_draft');
  const hasPublishPermission = hasPermission('cms_management_publish');
  const hasRollbackPermission = hasPermission('cms_management_rollback');

  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [isWorkflowOpen, setIsWorkflowOpen] = useState(false);
  const [generationMode, setGenerationMode] = useState<
    'overwrite_draft' | 'create_new_draft_version'
  >('overwrite_draft');
  const [draftEditors, setDraftEditors] = useState<
    Record<string, DraftEditorState>
  >({});

  const [generateDrafts, { isLoading: isGeneratingDrafts }] =
    useGenerateCMSFAQDraftsMutation();
  const [saveDraft, { isLoading: isSavingDraft }] =
    useUpdateCMSFAQTranslationDraftMutation();
  const [publishTranslation, { isLoading: isPublishingTranslation }] =
    usePublishCMSFAQTranslationMutation();
  const [rollbackTranslation, { isLoading: isRollingBackTranslation }] =
    useRollbackCMSFAQTranslationMutation();

  const variants = item.translation_variants || [];
  const sourceVariant = variants.find(
    (variant) => variant.language === item.source_language
  );
  const targetLanguages = useMemo(
    () =>
      I18N_LANGUAGES.filter(
        (language) => language.code !== item.source_language
      ),
    [item.source_language]
  );

  const variantMap = useMemo(
    () =>
      variants.reduce<Record<string, CMSFAQTranslationVariant>>(
        (acc, variant) => {
          acc[variant.language] = variant;
          return acc;
        },
        {}
      ),
    [variants]
  );

  useEffect(() => {
    setDraftEditors((previous) =>
      targetLanguages.reduce<Record<string, DraftEditorState>>(
        (acc, language) => {
          const variant = variantMap[language.code];
          const currentEditor = previous[language.code];
          const seed = getEditorSeed(variant);

          acc[language.code] = currentEditor
            ? {
                ...currentEditor,
                title: currentEditor.isEditingTitle
                  ? currentEditor.title
                  : seed.title,
                content: currentEditor.isEditingContent
                  ? currentEditor.content
                  : seed.content,
              }
            : {
                ...seed,
                isExpanded: false,
                isEditingTitle: false,
                isEditingContent: false,
              };

          return acc;
        },
        {}
      )
    );
  }, [targetLanguages, variantMap]);

  const handleLanguageSelection = (languageCode: string) => {
    setSelectedLanguages((previous) =>
      previous.includes(languageCode)
        ? previous.filter((language) => language !== languageCode)
        : [...previous, languageCode]
    );
  };

  const handleBulkGenerate = async () => {
    if (!selectedLanguages.length) return;

    try {
      await generateDrafts({
        id: item.id,
        target_languages: selectedLanguages,
        source_language: item.source_language,
        mode: generationMode,
      }).unwrap();
      enqueueSnackbar('Translation drafts generated successfully', {
        variant: 'success',
      });
      setSelectedLanguages([]);
      await onRefresh();
    } catch {
      enqueueSnackbar('Failed to generate translation drafts', {
        variant: 'error',
      });
    }
  };

  const toggleLanguageDetails = (languageCode: string) => {
    setDraftEditors((previous) => ({
      ...previous,
      [languageCode]: previous[languageCode]
        ? {
            ...previous[languageCode],
            isExpanded: !previous[languageCode].isExpanded,
            isEditingTitle: previous[languageCode].isExpanded
              ? false
              : previous[languageCode].isEditingTitle,
            isEditingContent: previous[languageCode].isExpanded
              ? false
              : previous[languageCode].isEditingContent,
          }
        : {
            ...getEditorSeed(variantMap[languageCode]),
            isExpanded: true,
            isEditingTitle: false,
            isEditingContent: false,
          },
    }));
  };

  const toggleFieldEditor = (
    languageCode: string,
    field: 'title' | 'content'
  ) => {
    setDraftEditors((previous) => {
      const currentState = previous[languageCode] || {
        ...getEditorSeed(variantMap[languageCode]),
        isExpanded: true,
        isEditingTitle: false,
        isEditingContent: false,
      };

      return {
        ...previous,
        [languageCode]: {
          ...currentState,
          isExpanded: true,
          isEditingTitle:
            field === 'title'
              ? !currentState.isEditingTitle
              : currentState.isEditingTitle,
          isEditingContent:
            field === 'content'
              ? !currentState.isEditingContent
              : currentState.isEditingContent,
        },
      };
    });
  };

  const updateEditorField = (
    languageCode: string,
    field: 'title' | 'content',
    value: string
  ) => {
    setDraftEditors((previous) => ({
      ...previous,
      [languageCode]: {
        ...(previous[languageCode] || {
          title: '',
          content: '',
          isExpanded: true,
          isEditingTitle: false,
          isEditingContent: false,
        }),
        [field]: value,
      },
    }));
  };

  const closeFieldEditors = (languageCode: string) => {
    setDraftEditors((previous) => {
      const currentState = previous[languageCode];
      if (!currentState) {
        return previous;
      }

      return {
        ...previous,
        [languageCode]: {
          ...currentState,
          isExpanded: true,
          isEditingTitle: false,
          isEditingContent: false,
        },
      };
    });
  };

  const handleSaveDraft = async (languageCode: string) => {
    const editor = draftEditors[languageCode];
    if (!editor?.title.trim() || !editor?.content.trim()) {
      enqueueSnackbar('Draft title and content are required', {
        variant: 'warning',
      });
      return;
    }

    try {
      await saveDraft({
        id: item.id,
        language: languageCode,
        title: editor.title,
        content: editor.content,
      }).unwrap();
      closeFieldEditors(languageCode);
      enqueueSnackbar('Draft saved successfully', { variant: 'success' });
      await onRefresh();
    } catch {
      enqueueSnackbar('Failed to save translation draft', {
        variant: 'error',
      });
    }
  };

  const handlePublish = async (languageCode: string) => {
    try {
      await publishTranslation({
        id: item.id,
        language: languageCode,
      }).unwrap();
      closeFieldEditors(languageCode);
      enqueueSnackbar('Translation published successfully', {
        variant: 'success',
      });
      await onRefresh();
    } catch {
      enqueueSnackbar('Failed to publish translation', {
        variant: 'error',
      });
    }
  };

  const handleRollback = async (languageCode: string) => {
    try {
      await rollbackTranslation({
        id: item.id,
        language: languageCode,
      }).unwrap();
      closeFieldEditors(languageCode);
      enqueueSnackbar('Previous published version restored', {
        variant: 'success',
      });
      await onRefresh();
    } catch {
      enqueueSnackbar('Failed to restore previous published version', {
        variant: 'error',
      });
    }
  };

  const isAnyMutationRunning =
    isGeneratingDrafts ||
    isSavingDraft ||
    isPublishingTranslation ||
    isRollingBackTranslation;

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-coal-100 dark:bg-coal-600 contrast:border-coal-100 contrast:bg-coal-600">
      <div className="border-b border-gray-200 px-4 py-4 dark:border-coal-100 contrast:border-coal-100">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 contrast:text-white">
              Translation Workflow
            </h4>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 contrast:text-white/80">
              Auto-generated translations stay as drafts until an editor reviews
              and publishes them.
            </p>
          </div>
          <span className="badge badge-success badge-outline">
            Source: {getLanguageLabel(item.source_language)}
          </span>
        </div>
        {sourceVariant?.last_updated_at && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 contrast:text-white/70">
            Source last updated{' '}
            {formatWorkflowTimestamp(sourceVariant.last_updated_at)}
          </p>
        )}
        <div className="mt-4 flex justify-end">
          <button
            className="btn btn-sm btn-light"
            onClick={() => setIsWorkflowOpen((previous) => !previous)}
          >
            <KeenIcon
              icon={isWorkflowOpen ? 'minus-squared' : 'plus-squared'}
              className="mr-2"
            />
            {isWorkflowOpen
              ? 'Hide Translation Workflow'
              : 'Open Translation Workflow'}
          </button>
        </div>
      </div>

      {isWorkflowOpen && (
        <div className="p-4 space-y-6">
          <div className="rounded-xl border border-gray-200 p-4 space-y-4 dark:border-coal-100 dark:bg-coal-500 contrast:border-coal-100 contrast:bg-coal-500">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100 contrast:text-white">
                  Generate Draft Translations
                </h5>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 contrast:text-white/80">
                  Select target languages, then choose how existing drafts
                  should be handled.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={generationMode}
                  onChange={(event) =>
                    setGenerationMode(
                      event.target.value as
                        | 'overwrite_draft'
                        | 'create_new_draft_version'
                    )
                  }
                  className="select select-sm select-bordered"
                  disabled={!hasDraftPermission || isAnyMutationRunning}
                >
                  <option value="overwrite_draft">Overwrite draft</option>
                  <option value="create_new_draft_version">
                    Create new draft version
                  </option>
                </select>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleBulkGenerate}
                  disabled={
                    !hasDraftPermission ||
                    !selectedLanguages.length ||
                    isAnyMutationRunning
                  }
                >
                  {isGeneratingDrafts ? 'Generating...' : 'Generate Drafts'}
                </button>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {targetLanguages.map((language) => {
                const variant = variantMap[language.code];
                const isSelected = selectedLanguages.includes(language.code);

                return (
                  <label
                    key={language.code}
                    className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5 dark:bg-primary/15 contrast:bg-primary/20'
                        : 'border-gray-200 bg-white dark:border-coal-100 dark:bg-coal-600 contrast:border-coal-100 contrast:bg-coal-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm mt-0.5"
                      checked={isSelected}
                      onChange={() => handleLanguageSelection(language.code)}
                      disabled={!hasDraftPermission || isAnyMutationRunning}
                    />
                    <img
                      src={language.flag}
                      alt={language.label}
                      className="w-5 h-5 rounded-sm mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-800 dark:text-gray-100 contrast:text-white">
                          {language.label}
                        </span>
                        <span
                          className={
                            statusClasses[variant?.status || 'missing']
                          }
                        >
                          {(variant?.status || 'missing').toUpperCase()}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 contrast:text-white/70">
                        {variant?.last_updated_label
                          ? `Last updated by ${variant.last_updated_label}`
                          : 'No translation workflow activity yet'}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>

            {!hasDraftPermission && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 contrast:border-amber-700 contrast:bg-amber-950/40 contrast:text-amber-100">
                You can view translation states, but generating and editing
                drafts requires the CMS FAQ draft permission.
              </p>
            )}
          </div>

          <div className="space-y-4">
            {targetLanguages.map((language) => {
              const variant = variantMap[language.code];
              const editorState = draftEditors[language.code] || {
                title: '',
                content: '',
                isExpanded: false,
                isEditingTitle: false,
                isEditingContent: false,
              };

              return (
                <div
                  key={language.code}
                  className="overflow-hidden rounded-xl border border-gray-200 dark:border-coal-100 contrast:border-coal-100"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-coal-100 dark:bg-coal-500 contrast:border-coal-100 contrast:bg-coal-500">
                    <div className="flex items-start gap-3">
                      <img
                        src={getLanguageFlag(language.code)}
                        alt={language.label}
                        className="w-6 h-6 rounded-sm mt-0.5"
                      />
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-gray-800 dark:text-gray-100 contrast:text-white">
                            {getLanguageLabel(language.code)}
                          </span>
                          <span
                            className={
                              statusClasses[variant?.status || 'missing']
                            }
                          >
                            {(variant?.status || 'missing').toUpperCase()}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 contrast:text-white/80">
                          {variant?.last_updated_label
                            ? `${variant.last_updated_label} • ${formatWorkflowTimestamp(
                                variant?.last_updated_at
                              )}`
                            : 'Missing translation'}
                        </p>
                        {variant?.status === 'draft' &&
                          variant.has_published_version && (
                            <p className="mt-1 text-xs text-amber-700 dark:text-amber-200 contrast:text-amber-100">
                              A published version is still live until this draft
                              is published.
                            </p>
                          )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => toggleLanguageDetails(language.code)}
                        disabled={isAnyMutationRunning}
                      >
                        <KeenIcon
                          icon={
                            editorState.isExpanded
                              ? 'minus-squared'
                              : 'plus-squared'
                          }
                          className="mr-2"
                        />
                        {editorState.isExpanded
                          ? 'Close Details'
                          : 'Open Details'}
                      </button>
                      {hasPublishPermission && variant?.draft && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handlePublish(language.code)}
                          disabled={isAnyMutationRunning}
                        >
                          Publish
                        </button>
                      )}
                      {hasRollbackPermission && variant?.can_rollback && (
                        <button
                          className="btn btn-sm btn-light"
                          onClick={() => handleRollback(language.code)}
                          disabled={isAnyMutationRunning}
                        >
                          Rollback
                        </button>
                      )}
                    </div>
                  </div>

                  {editorState.isExpanded && (
                    <div className="p-4 space-y-4">
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-coal-100 dark:bg-coal-500 contrast:border-coal-100 contrast:bg-coal-500">
                        <div className="space-y-3">
                          <div>
                            <p className="form-label">Draft Title</p>
                            {editorState.isEditingTitle ? (
                              <input
                                type="text"
                                value={editorState.title}
                                onChange={(event) =>
                                  updateEditorField(
                                    language.code,
                                    'title',
                                    event.target.value
                                  )
                                }
                                className="input input-bordered w-full"
                                placeholder={`Enter ${getLanguageLabel(
                                  language.code
                                )} FAQ title`}
                                disabled={
                                  !hasDraftPermission || isAnyMutationRunning
                                }
                              />
                            ) : (
                              <div className="min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-800 dark:border-coal-100 dark:bg-coal-600 dark:text-gray-100 contrast:border-coal-100 contrast:bg-coal-600 contrast:text-white">
                                {variant?.draft?.title ||
                                  variant?.published?.title ||
                                  'No translation available'}
                              </div>
                            )}
                          </div>
                          {hasDraftPermission && (
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() =>
                                toggleFieldEditor(language.code, 'title')
                              }
                              disabled={isAnyMutationRunning}
                            >
                              <KeenIcon
                                icon={
                                  editorState.isEditingTitle
                                    ? 'cross'
                                    : 'pencil'
                                }
                                className="mr-2"
                              />
                              {editorState.isEditingTitle
                                ? 'Close Title'
                                : 'Edit Title'}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-coal-100 dark:bg-coal-500 contrast:border-coal-100 contrast:bg-coal-500">
                        <div className="space-y-3">
                          <div>
                            <p className="form-label">Draft Description</p>
                            {editorState.isEditingContent ? (
                              <Editor
                                tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                                value={editorState.content}
                                init={{ ...tinymceInit, height: 240 }}
                                onEditorChange={(content: string) =>
                                  updateEditorField(
                                    language.code,
                                    'content',
                                    content
                                  )
                                }
                                textareaName={`cms-faq-draft-${language.code}`}
                                disabled={
                                  !hasDraftPermission || isAnyMutationRunning
                                }
                              />
                            ) : (
                              <div
                                className="min-h-[140px] rounded-lg border border-gray-200 bg-white px-4 py-4 text-gray-700 dark:border-coal-100 dark:bg-coal-600 dark:text-gray-100 contrast:border-coal-100 contrast:bg-coal-600 contrast:text-white prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    variant?.draft?.content ||
                                    variant?.published?.content ||
                                    '<p>No translation available</p>'
                                  ),
                                }}
                              />
                            )}
                          </div>
                          {hasDraftPermission && (
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() =>
                                toggleFieldEditor(language.code, 'content')
                              }
                              disabled={isAnyMutationRunning}
                            >
                              <KeenIcon
                                icon={
                                  editorState.isEditingContent
                                    ? 'cross'
                                    : 'pencil'
                                }
                                className="mr-2"
                              />
                              {editorState.isEditingContent
                                ? 'Close Description'
                                : 'Edit Description'}
                            </button>
                          )}
                        </div>
                      </div>

                      {hasDraftPermission && (
                        <div className="flex justify-end">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSaveDraft(language.code)}
                            disabled={isAnyMutationRunning}
                          >
                            Save Draft
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSFAQTranslationWorkflowSection;
